/**
 * Client-side pagination over an in-memory list.
 *
 * Used by sections that already hold the full dataset (e.g. the Pending /
 * Stalled transfer sections, which fetch all pending adoptions up front and
 * partition them by age). Mirrors the shape the section tables already expect
 * from the server-paginated `useRescueDevices` hook so they render unchanged.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

export interface UseClientPaginationResult<T> {
  /** Items on the current page. */
  pageItems: T[];
  /** Total number of items across all pages. */
  totalCount: number;
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
  resetPage: () => void;
}

export default function useClientPagination<T>(items: T[], pageSize: number): UseClientPaginationResult<T> {
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = items.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Clamp the current page when the list shrinks (mutation, filter change) so we
  // never strand the user on an empty page past the end.
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, currentPage, pageSize]);

  const goToPage = useCallback((page: number) => {
    setCurrentPage(Math.max(1, page));
  }, []);

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return { pageItems, totalCount, currentPage, totalPages, goToPage, resetPage };
}
